import axiosInstance from './axios';
import type { Vehicle } from './vehicles.api';
import { db, MovementType } from '../db'; // Import DB
import { v4 as uuidv4 } from 'uuid'; // Import UUID
import { calculateRate } from '../utils/billing'; // Import billing logic
import { toast } from 'sonner';

export interface Movement {
    id: string;
    tipoMovimiento: 'ENTRADA' | 'SALIDA';
    horaEntrada: string;
    horaSalida?: string;
    duracion?: string;
    tarifa?: number;
    status: 'ACTIVO' | 'FINALIZADO';
    esContrato: boolean;
    usaCupoContrato?: boolean; // Indica si usa el cupo gratuito del contrato
    vehiculo: Vehicle;
    operador: {
        nombre: string;
    };
    payment?: any; // Para re-impresión
}

export interface RegisterEntryData {
    placa: string;
    tipo?: string;
}

// Datos del cliente para facturación
export interface InvoiceClientData {
    tipoDocumento: string; // CC, NIT, CE, PASAPORTE
    numeroDocumento: string;
    nombre: string;
    email?: string;
    telefono?: string;
    direccion?: string;
    ciudad?: string;
    ciudadId?: string;
    saveClient?: boolean; // Guardar para uso futuro
}

export interface RegisterExitData {
    placa: string;
    tarifaCobrada?: number;
    metodoPago?: 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'DEBITO' | 'CREDITO';
    invoiceType?: 'CONSUMIDOR_FINAL' | 'CLIENTE_IDENTIFICADO';
    clientData?: InvoiceClientData;
}

export const getActiveMovements = async (): Promise<Movement[]> => {
    if (!navigator.onLine) {
        // Offline: Fetch from local DB
        const localMovements = await db.movements
            .where('status').equals('ACTIVO')
            .filter(m => m.companyId === getCompanyIdFromStorage()) // Helper needed
            .reverse()
            .sortBy('horaEntrada');

        return localMovements.map(m => ({
            id: m.id,
            tipoMovimiento: m.tipoMovimiento,
            horaEntrada: m.horaEntrada,
            horaSalida: m.horaSalida,
            status: m.status,
            esContrato: m.esContrato,
            vehiculo: { placa: m.placa, tipo: 'AUTO', id: m.vehiculoId || 'temp' } as any, // Mock vehicle
            operador: { nombre: 'Operador Offline' }
        }));
    }
    const response = await axiosInstance.get<Movement[]>('/movements/active');
    return response.data;
};

export const getHistoryMovements = async (): Promise<Movement[]> => {
    const response = await axiosInstance.get<Movement[]>('/movements/history');
    return response.data;
};

export interface Ticket {
    id: string;
    numero: string;
    horaEntrada: string;
    vehiculo: Vehicle;
    operador: { nombre: string };
    company: { nombre: string; nit?: string; direccion?: string };
}

export interface RegisterEntryResponse {
    movement: Movement;
    ticket: Ticket;
    contract?: any;
}

export interface PreviewExitResponse {
    vehiculo: Vehicle;
    movement: Movement;
    horaEntrada: string;
    horaSalida: string;
    duracion: string;
    duracionMinutos: number;
    rate: any;
    total: number;
    breakdown: string;
}

// Respuesta de factura electrónica
export interface ElectronicInvoice {
    id: string;
    numero: string;
    cufe?: string;
    tipoCliente: 'CONSUMIDOR_FINAL' | 'CLIENTE_IDENTIFICADO';
    clienteNombre: string;
    clienteDocumento: string;
    monto: number;
    pdfUrl?: string;
    xmlUrl?: string;
    qrCode?: string;
    estado: 'EMITIDA' | 'ERROR' | 'PENDIENTE';
    errorMensaje?: string;
}

export interface RegisterExitResponse {
    movement: Movement;
    payment: any;
    invoice?: ElectronicInvoice;
}

export const registerEntry = async (data: RegisterEntryData): Promise<RegisterEntryResponse> => {
    if (!navigator.onLine) {
        const companyId = getCompanyIdFromStorage();
        if (!companyId) throw new Error("No company ID found");

        const id = uuidv4();
        const ticketNo = `OFF-${Date.now().toString().slice(-6)}`;
        const now = new Date().toISOString();

        // Check for active contract locally
        const activeContract = await db.contracts
            .where({ companyId, placa: data.placa, status: 'ACTIVO' })
            .filter(c => new Date(c.fechaInicio) <= new Date(now) && new Date(c.fechaFin) >= new Date(now))
            .first();

        const esContrato = !!activeContract;

        // 1. Save Movement
        await db.movements.add({
            id,
            tipoMovimiento: MovementType.ENTRADA,
            horaEntrada: now,
            placa: data.placa,
            status: 'ACTIVO',
            esContrato: esContrato,
            synced: false,
            companyId
        });

        // 2. Save Pending Action
        await db.pendingQueue.add({
            id: uuidv4(),
            type: 'ENTRY',
            payload: { ...data, esContrato }, // Pass contract flag if needed by backend or just rely on backend re-check
            timestamp: Date.now(),
            companyId
        });

        const mockVehicle: Vehicle = {
            id: uuidv4(),
            placa: data.placa,
            tipo: (data.tipo as any) || 'AUTO',
            companyId,
            status: 'DENTRO',
            createdAt: now,
            updatedAt: now
        } as unknown as Vehicle;

        const mockTicket: Ticket = {
            id: uuidv4(),
            numero: ticketNo,
            horaEntrada: now,
            vehiculo: mockVehicle,
            operador: { nombre: 'Offline' },
            company: { nombre: 'Offline Mode' }
        };

        toast.warning(esContrato
            ? `Ingreso registrado localmente (CONTRATO: ${activeContract?.clienteNombre})`
            : 'Ingreso registrado localmente (Offline)'
        );

        return {
            movement: {
                id,
                tipoMovimiento: 'ENTRADA',
                horaEntrada: now,
                status: 'ACTIVO',
                esContrato: esContrato,
                vehiculo: mockVehicle,
                operador: { nombre: 'Offline' }
            },
            ticket: mockTicket,
            contract: activeContract // Mock contract response
        };
    }
    const response = await axiosInstance.post<RegisterEntryResponse>('/movements/entry', data);
    return response.data;
};

export const previewExit = async (placa: string): Promise<PreviewExitResponse> => {
    if (!navigator.onLine) {
        const companyId = getCompanyIdFromStorage();
        const movement = await db.movements
            .where({ placa: placa, status: 'ACTIVO' })
            .first(); // Assuming status is indexed or filter

        if (!movement) throw new Error('Vehículo no encontrado localmente');

        // Find Rate
        const rate = await db.rates
            .where({ companyId: companyId }) // We need to filter by vehicle type roughly
            .toArray();

        // Assuming default 'AUTO' if not present in movement (we stored local movement without explicit type sometimes)
        // Ideally DB should store vehicle type.
        // For now take first rate or default.
        const vehicleType = 'AUTO';
        const applicableRate = rate.find(r => r.tipoVehiculo === vehicleType);

        const billing = calculateRate(movement.horaEntrada, new Date().toISOString(), vehicleType, applicableRate);

        const mockVehicle: Vehicle = {
            id: movement.vehiculoId || 'temp',
            placa: movement.placa,
            tipo: 'AUTO',
            companyId: companyId || '',
            status: 'DENTRO',
            createdAt: movement.horaEntrada,
            updatedAt: new Date().toISOString()
        } as unknown as Vehicle;

        return {
            vehiculo: mockVehicle,
            movement: {
                id: movement.id,
                tipoMovimiento: 'ENTRADA',
                horaEntrada: movement.horaEntrada,
                status: 'ACTIVO',
                esContrato: movement.esContrato,
                vehiculo: mockVehicle,
                operador: { nombre: 'Offline' }
            },
            horaEntrada: movement.horaEntrada,
            horaSalida: new Date().toISOString(),
            duracion: billing.duracionTexto,
            duracionMinutos: billing.durationMinutes,
            rate: applicableRate,
            total: billing.total,
            breakdown: billing.breakdown
        };
    }
    const response = await axiosInstance.get<PreviewExitResponse>(`/movements/preview-exit/${placa}`);
    return response.data;
};

export const registerExit = async (data: RegisterExitData): Promise<RegisterExitResponse> => {
    if (!navigator.onLine) {
        const companyId = getCompanyIdFromStorage();

        const movement = await db.movements
            .where({ placa: data.placa, status: 'ACTIVO' })
            .first();

        if (movement) {
            await db.movements.update(movement.id, {
                status: 'FINALIZADO',
                horaSalida: new Date().toISOString()
            });

            // 2. Save Pending Action
            await db.pendingQueue.add({
                id: uuidv4(),
                type: 'EXIT',
                payload: data,
                timestamp: Date.now(),
                companyId: companyId || ''
            });

            const mockPayment = {
                id: uuidv4(),
                total: data.tarifaCobrada || 0,
                metodoPago: data.metodoPago || 'EFECTIVO',
                status: 'PAGADO',
                createdAt: new Date().toISOString()
            };

            toast.warning('Salida registrada localmente (Offline)');

            return {
                movement: {
                    ...movement,
                    status: 'FINALIZADO',
                    vehiculo: { placa: data.placa } as any,
                    operador: { nombre: 'Offline' }
                } as any,
                payment: mockPayment
            };
        }
    }
    const response = await axiosInstance.post<RegisterExitResponse>('/movements/exit', data);
    return response.data;
};


// Helper to get company ID from localStorage (JWT or stored user info)
const getCompanyIdFromStorage = () => {
    try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const user = JSON.parse(storedUser);
            return user.companyId;
        }
    } catch (e) { console.error(e); }
    return null;
};

